import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Progress, Spinner } from '@nextui-org/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { ErrorLine, TabIntro } from '../aiAccess/shared';
import type { Batch, BatchCounts, BulkRows, EnrichmentClient } from './api';
import {
  blockedCsv,
  csvName,
  downloadCsv,
  finishCounts,
  finishOutcome,
  resultsCsv,
  runBulkLookup,
  sha256Hex,
  type RowResult,
} from './bulk';
import { OUTCOMES, warrantyStatus } from './ProductView';
import { FilePicker } from './FilePicker';

/** Rows shown live while a run goes (the latest ones); the CSV has them all. */
export const BULK_ROWS_SHOWN = 200;
const INVALID_SHOWN = 50;

type Phase = 'idle' | 'running' | 'finishing' | 'done';

/**
 * Bulk lookup: the customer's list (first column the OEM, second the serial number) is read by the
 * service, then its rows are looked up from here, three at a time, in one batch. Dell rows are
 * blocked (its site blocks the agent): they download as CSV, to enrich locally and import.
 */
export function BulkLookupSection({ client, onFinished }: {
  readonly client: EnrichmentClient | null;
  readonly onFinished: () => void;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<BulkRows | null>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [batch, setBatch] = useState<Batch | null>(null);
  const [results, setResults] = useState<Array<RowResult | null>>([]);
  const [started, setStarted] = useState(0);
  const [summary, setSummary] = useState<{ counts: BatchCounts; cancelled: boolean } | null>(null);
  const [finishError, setFinishError] = useState<string | null>(null);
  const runRef = useRef<AbortController | null>(null);
  const readRef = useRef<AbortController | null>(null);
  // Leaving the page cancels a run; it still finishes its batch as cancelled.
  useEffect(() => () => {
    runRef.current?.abort();
    readRef.current?.abort();
  }, []);

  const rows = parsed?.rows ?? [];
  const busy = reading || phase === 'running' || phase === 'finishing';

  const choose = async (next: File) => {
    if (!client) return;
    readRef.current?.abort();
    const request = new AbortController();
    readRef.current = request;
    setFile(next);
    setParsed(null);
    setError(null);
    setPhase('idle');
    setBatch(null);
    setResults([]);
    setSummary(null);
    setFinishError(null);
    setReading(true);
    try {
      const read = await client.bulkRows(next, request.signal);
      if (!request.signal.aborted) setParsed(read);
    } catch (e) {
      if (!request.signal.aborted) setError((e as Error).message || t('Something went wrong.'));
    } finally {
      if (!request.signal.aborted) setReading(false);
    }
  };

  const start = async () => {
    if (!client || !file || rows.length === 0) return;
    const run = new AbortController();
    runRef.current = run;
    setError(null);
    setFinishError(null);
    setSummary(null);
    setResults(rows.map(() => null));
    setStarted(0);
    setPhase('running');
    let created: Batch;
    try {
      created = await client.startBatch({
        kind: 'bulk_lookup',
        file_name: file.name,
        file_sha256: await sha256Hex(file),
        row_count: rows.length,
      });
      if (!created?.id) throw new Error(t('The service did not return a batch.'));
    } catch (e) {
      setError((e as Error).message || t('Something went wrong.'));
      setPhase('idle');
      return;
    }
    const batchId = created.id;
    setBatch(created);
    const outcome = await runBulkLookup(
      rows,
      (row, signal) => client.lookup({ serial_number: row.serial_number, oem: row.oem, batch_id: batchId }, signal),
      {
        signal: run.signal,
        onStart: () => setStarted((count) => count + 1),
        onResult: (index, result) => setResults((prev) => {
          const next = [...prev];
          next[index] = result;
          return next;
        }),
      },
    );
    setPhase('finishing');
    const counts = finishCounts(outcome.results);
    try {
      setBatch(await client.finishBatch(batchId, { outcome: finishOutcome(outcome), counts }));
    } catch (e) {
      setFinishError(t('The batch could not be closed: {{reason}}', { reason: (e as Error).message }));
    }
    setResults(outcome.results);
    setSummary({ counts, cancelled: outcome.cancelled });
    setPhase('done');
    onFinished();
  };

  const done = results.filter(Boolean).length;
  const shownFrom = Math.max(0, started - BULK_ROWS_SHOWN);
  const live = rows.slice(shownFrom, started).map((row, offset) => ({ row, result: results[shownFrom + offset] ?? null }));
  const failures = results.filter((result) => result && (result.outcome === 'needs_info' || result.outcome === 'error')).length;
  const fileName = file?.name ?? 'bulk-lookup';

  return (
    <section aria-label={t('Bulk lookup')} className="flex flex-col gap-3">
      <TabIntro title={t('Bulk lookup')}>
        <span className="text-sm text-default-500">
          {t('Upload the customer’s list as Excel or CSV: first column the OEM, second the serial number. Other columns are ignored.')}
        </span>
      </TabIntro>
      <FilePicker
        label={t('Bulk lookup file')}
        buttonLabel={t('Choose a file')}
        accept=".xlsx,.csv"
        fileName={file?.name ?? null}
        isDisabled={!client || busy}
        onChoose={(next) => void choose(next)}
      />
      {reading && <Spinner size="sm" className="self-start" />}
      <ErrorLine message={error} />

      {parsed && (
        <div className="flex flex-col gap-3">
          <div role="group" aria-label={t('Rows in the file')} className="flex flex-wrap items-center gap-2 text-sm">
            <Chip size="sm" variant="flat" color="primary">{t('{{count}} to look up', { count: rows.length })}</Chip>
            <Chip size="sm" variant="flat" color="warning">{t('{{count}} blocked', { count: parsed.blocked.length })}</Chip>
            <Chip size="sm" variant="flat" color="danger">{t('{{count}} invalid', { count: parsed.invalid.length })}</Chip>
          </div>

          {parsed.blocked.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-medium border border-warning-200 bg-warning-50 p-3 text-sm">
              <span>
                {t('Dell’s site blocks the agent, so these serials are not looked up here. Enrich them locally and import the answers below.')}
              </span>
              <CtyButton variant="flat" size="sm" onPress={() => downloadCsv(csvName(fileName, 'blocked'), blockedCsv(parsed.blocked))}>
                {t('Download these serials (CSV)')}
              </CtyButton>
            </div>
          )}

          {parsed.invalid.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" aria-label={t('Invalid rows')}>
                <thead className="text-xs text-default-500">
                  <tr>
                    <th className="py-1 pr-4 font-medium">{t('Row')}</th>
                    <th className="py-1 pr-4 font-medium">{t('OEM')}</th>
                    <th className="py-1 pr-4 font-medium">{t('Serial')}</th>
                    <th className="py-1 font-medium">{t('Reason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.invalid.slice(0, INVALID_SHOWN).map((row) => (
                    <tr key={row.row} className="border-t border-default-100">
                      <td className="py-1 pr-4 text-xs">{row.row}</td>
                      <td className="py-1 pr-4 text-xs">{row.oem_text || '—'}</td>
                      <td className="py-1 pr-4 font-mono text-xs">{row.serial_number || '—'}</td>
                      <td className="py-1 text-xs text-default-500">{row.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsed.invalid.length > INVALID_SHOWN && (
                <p className="text-xs text-default-500">
                  {t('Showing {{shown}} of {{total}} invalid rows.', { shown: INVALID_SHOWN, total: parsed.invalid.length })}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <CtyButton color="primary" size="sm" onPress={() => void start()} isDisabled={!client || busy || rows.length === 0}>
              {phase === 'done' ? t('Start again') : t('Start')}
            </CtyButton>
            {phase === 'running' && (
              <CtyButton variant="flat" color="danger" size="sm" onPress={() => runRef.current?.abort()}>
                {t('Cancel')}
              </CtyButton>
            )}
            {phase === 'finishing' && <Spinner size="sm" aria-label={t('Closing the batch')} />}
          </div>
        </div>
      )}

      {phase !== 'idle' && rows.length > 0 && (
        <div className="flex flex-col gap-3">
          <Progress
            aria-label={t('Bulk lookup progress')}
            size="sm"
            value={rows.length ? (done / rows.length) * 100 : 0}
            color={summary?.cancelled ? 'warning' : 'primary'}
          />
          <span className="text-xs text-default-500">
            {t('{{done}} of {{total}} looked up', { done, total: rows.length })}
            {batch?.state ? ` · ${t('batch {{state}}', { state: batch.state })}` : ''}
          </span>

          {summary && (
            <div role="status" className="flex flex-col gap-2">
              <span className="text-sm">
                {summary.cancelled ? t('Cancelled.') : t('Done.')}{' '}
                {t('{{found}} found, {{notFound}} not found, {{failed}} failed, {{skipped}} skipped.', {
                  found: summary.counts.found,
                  notFound: summary.counts.not_found,
                  failed: summary.counts.failed,
                  skipped: summary.counts.skipped,
                })}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <CtyButton variant="flat" size="sm" onPress={() => downloadCsv(csvName(fileName, 'results'), resultsCsv(rows, results))}>
                  {t('Download results (CSV)')}
                </CtyButton>
                <CtyButton
                  variant="flat"
                  size="sm"
                  onPress={() => downloadCsv(csvName(fileName, 'failures'), resultsCsv(rows, results, true))}
                  isDisabled={failures === 0}
                >
                  {t('Download failures (CSV)')}
                </CtyButton>
              </div>
            </div>
          )}
          <ErrorLine message={finishError} />

          {live.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" aria-label={t('Bulk lookup results')}>
                <thead className="text-xs text-default-500">
                  <tr>
                    <th className="py-1 pr-4 font-medium">{t('Row')}</th>
                    <th className="py-1 pr-4 font-medium">{t('OEM')}</th>
                    <th className="py-1 pr-4 font-medium">{t('Serial')}</th>
                    <th className="py-1 pr-4 font-medium">{t('Result')}</th>
                    <th className="py-1 pr-4 font-medium">{t('Product')}</th>
                    <th className="py-1 pr-4 font-medium">{t('Warranty')}</th>
                    <th className="py-1 font-medium">{t('Error')}</th>
                  </tr>
                </thead>
                <tbody>
                  {live.map(({ row, result }) => {
                    const shown = result ? OUTCOMES[result.outcome] : null;
                    const warranty = result?.answer?.warranty;
                    return (
                      <tr key={row.row} className="border-t border-default-100 align-top">
                        <td className="py-1 pr-4 text-xs">{row.row}</td>
                        <td className="py-1 pr-4 text-xs">{row.oem_text || row.oem || '—'}</td>
                        <td className="py-1 pr-4 font-mono text-xs">{row.serial_number}</td>
                        <td className="py-1 pr-4">
                          {shown ? (
                            <Chip size="sm" variant="flat" color={shown.color}>{t(shown.label)}</Chip>
                          ) : (
                            <span className="text-xs text-default-500">{phase === 'running' ? t('Looking up…') : t('Skipped')}</span>
                          )}
                        </td>
                        <td className="py-1 pr-4 text-xs">{result?.answer?.product?.product_name || result?.answer?.product?.product_number || '—'}</td>
                        <td className="py-1 pr-4 text-xs">
                          {warranty && result?.outcome === 'found' ? (
                            <>
                              {t(warrantyStatus(warranty.status).label)}
                              {warranty.end_date ? ` · ${warranty.end_date}` : ''}
                            </>
                          ) : '—'}
                        </td>
                        <td className="py-1 text-xs text-default-500">{result?.error ?? ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {started > BULK_ROWS_SHOWN && (
                <p className="text-xs text-default-500">
                  {t('Showing the latest {{shown}} of {{total}} rows; the results CSV has them all.', { shown: BULK_ROWS_SHOWN, total: started })}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
